import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreatePaymentTerm, PaymentTerm, UpdatePaymentTerm } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// AP Phase C1 — PaymentTerm master hooks. Per-company presets drive each bill's
// due date (dueDate = billDate + netDays) and an optional early-payment discount
// window (discountPercent / discountDays). Cache is keyed by companyId so a
// DCC/TTOP/RCI switch yields independent caches (same convention as vendors/AR).
const key = (companyId: string | undefined, activeOnly: boolean) =>
  ['payment-terms', companyId, { activeOnly }] as const;

// List the company's payment terms. activeOnly defaults to true (the masters'
// pickers want active presets only); pass false for the master-admin table.
export function usePaymentTerms(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: key(companyId, activeOnly),
    queryFn: () =>
      api<PaymentTerm[]>(`/companies/${companyId}/payment-terms?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

// Invalidate every activeOnly variant of the company's payment-terms cache.
function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: ['payment-terms', companyId] });
}

export function useCreatePaymentTerm(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePaymentTerm) =>
      apiJson<CreatePaymentTerm, PaymentTerm>(
        'POST',
        `/companies/${companyId}/payment-terms`,
        data,
      ),
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useUpdatePaymentTerm(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePaymentTerm }) =>
      apiJson<UpdatePaymentTerm, PaymentTerm>(
        'PATCH',
        `/companies/${companyId}/payment-terms/${id}`,
        data,
      ),
    onSuccess: () => invalidate(qc, companyId),
  });
}

// Soft-delete — DELETE flips isActive=false on the server.
export function useDeactivatePaymentTerm(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, PaymentTerm>('DELETE', `/companies/${companyId}/payment-terms/${id}`),
    onSuccess: () => invalidate(qc, companyId),
  });
}
